import React, { useCallback, useEffect, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';

const MAX_REPLY = 2000;
const REFRESH_MS = 30000;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

// Council-related messages from students (T7): a squad's assigned auditors and the program administrators read them here and
// answer. The student reads the answer in the LMS and gets a short email saying there is a reply.
export default function EsgSupportInbox({ onError, onSuccess }) {
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState(null);

  // `quiet` refreshes in the background (no "Loading..." flash, no error toast) so the list follows changes made elsewhere.
  const load = useCallback((quiet = false) => {
    if (!quiet) setLoading(true);
    return mentorApi.getEsgSupportMessages(status)
      .then(setRows)
      .catch(() => { if (!quiet) onError?.("Couldn't load support messages - try again."); })
      .finally(() => { if (!quiet) setLoading(false); });
  }, [status, onError]);

  useEffect(() => {
    load();
  }, [load]);

  // A message answered by someone else (another auditor, or an admin in Central's admin panel) moves tabs while this screen is
  // open, so the list is refreshed every half minute and when the tab is brought back.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState !== 'hidden') load(true); };
    const timer = setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  const send = async (row) => {
    const text = (drafts[row.id] || '').trim();
    if (!text) return;
    setSending(row.id);
    try {
      await mentorApi.replyToEsgSupportMessage(row.id, text);
      onSuccess?.('Reply sent.');
      setDrafts((d) => ({ ...d, [row.id]: '' }));
      await load();
    } catch (e) {
      onError?.("Couldn't send the reply - try again.");
      load();
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row">
        <h1>Support Inbox</h1>
      </div>
      <div className="esg-support__filters">
        {[['open', 'Waiting for a reply'], ['answered', 'Answered']].map(([id, label]) => (
          <button key={id} type="button" className={`esg-btn ${status === id ? 'esg-btn-primary' : 'esg-btn-secondary'}`} onClick={() => setStatus(id)}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="placeholder-card">Loading messages...</div>
      ) : rows.length === 0 ? (
        <div className="placeholder-card">{status === 'open' ? 'No messages are waiting for a reply.' : 'No answered messages yet.'}</div>
      ) : (
        rows.map((row) => (
          <div key={row.id} className="esg-support__card">
            <div className="esg-support__meta">
              <strong>{row.student_name || 'A student'}</strong>
              <span>{row.squad_name}</span>
              <span>{fmt(row.submitted_at)}</span>
              {row.reference && <span className="esg-support__ref-wrap">Request ID: <strong className="esg-support__ref">{row.reference}</strong></span>}
            </div>
            <div className="esg-support__message">{row.message}</div>
            {/* Newest reply first. */}
            {[...(row.replies || [])].reverse().map((r) => (
              <div key={r.id} className="esg-support__reply">
                <div className="esg-support__reply-by">Reply by {r.author_name || 'staff'}{r.source === 'central' ? ' (admin panel)' : ''} · {fmt(r.replied_at)}</div>
                <div>{r.body}</div>
              </div>
            ))}
            <div className="esg-support__form">
              <textarea
                className="form-control-textarea"
                rows={3}
                maxLength={MAX_REPLY}
                placeholder={row.status === 'answered' ? 'Write another reply to the student' : 'Write your reply to the student'}
                aria-label={`Reply to ${row.student_name || 'the student'}`}
                value={drafts[row.id] || ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [row.id]: e.target.value }))}
              />
              <button type="button" className="esg-btn esg-btn-primary" disabled={sending === row.id || !(drafts[row.id] || '').trim()} onClick={() => send(row)}>
                {sending === row.id ? 'Sending...' : 'Send reply'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
